import type { RichAircraft } from '../data/enrich';
import { minimapFovWedge, minimapPosition } from './geometry';

interface Props {
  user: { lat: number; lon: number };
  aircraft: RichAircraft[];
  azimuth: number;
  fovXDeg: number;
  rangeNm: number;
  size?: number;
}

const CLASS_COLOR: Record<string, string> = {
  commercial: '#38bdf8',
  military: '#f97316',
  private: '#a78bfa',
  government: '#fbbf24',
};

export function ArMinimap({ user, aircraft, azimuth, fovXDeg, rangeNm, size = 120 }: Props) {
  const pad = 8;
  const r = size / 2 - pad;
  const cx = size / 2;
  const cy = size / 2;

  const toSvg = (x: number, y: number) => ({
    sx: cx + x * r,
    sy: cy + y * r,
  });

  const wedge = minimapFovWedge(azimuth, fovXDeg, rangeNm);
  const wedgePath = wedge
    .map((p, i) => {
      const { sx, sy } = toSvg(p.x, p.y);
      return `${i === 0 ? 'M' : 'L'} ${sx} ${sy}`;
    })
    .join(' ');

  const dots: { sx: number; sy: number; color: string; key: string }[] = [];
  for (const ac of aircraft) {
    const pos = minimapPosition(user, ac, rangeNm);
    if (!pos) continue;
    const { sx, sy } = toSvg(pos.x, pos.y);
    dots.push({
      sx,
      sy,
      color: CLASS_COLOR[ac.flightClass] ?? '#94a3b8',
      key: ac.icao,
    });
  }

  const north = toSvg(0, -1.05);

  return (
    <div className="pointer-events-none overflow-hidden rounded-xl bg-bg-panel/90 ring-1 ring-bg-border backdrop-blur">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Range ring */}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#334155" strokeWidth={1} />
        <circle cx={cx} cy={cy} r={r * 0.5} fill="none" stroke="#1e293b" strokeWidth={0.5} strokeDasharray="2 3" />

        {/* FOV wedge */}
        <path d={`${wedgePath} Z`} fill="rgba(56,189,248,0.15)" stroke="rgba(56,189,248,0.5)" strokeWidth={1} />

        {/* Aircraft dots */}
        {dots.map((d) => (
          <circle key={d.key} cx={d.sx} cy={d.sy} r={2.5} fill={d.color} opacity={0.9} />
        ))}

        {/* User position */}
        <circle cx={cx} cy={cy} r={4} fill="#38bdf8" stroke="#0b1020" strokeWidth={1.5} />

        {/* North indicator */}
        <text x={north.sx} y={north.sy} textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize={8} fontFamily="monospace">
          N
        </text>
      </svg>
      <div className="border-t border-bg-border px-2 py-0.5 text-center font-mono text-[9px] text-slate-500">
        {Math.round(rangeNm)} nm
      </div>
    </div>
  );
}
